/**
 * Pure AAL-claim helpers (no I/O) — the source of truth for the server-side MFA gate.
 *
 * LOCKSTEP MIRROR: `supabase/functions/_shared/requireAal2.ts` re-implements
 * `decodeAalFromJwt` + `shouldBlockForAal2` verbatim (Deno can't import client JS).
 * Change one, change the other. This file is the Jest-tested reference;
 * `aalClaim.test.js` is the regression guard.
 *
 * DESIGN — mostly FAIL OPEN, with two narrow fail-closed exceptions (2026-09-25):
 * an undecodable token or a missing claim still returns "don't block" here, so a
 * decoder bug can never lock out a legitimate user on its own. The two exceptions:
 * (1) a payload that decodes and carries a `sub` (i.e. is shaped like a real user
 * access token) but has no valid `aal` claim is now treated as `aal1` rather than
 * pass-through — GoTrue always stamps `aal` on user tokens, so this shape only
 * occurs on a forged/foreign token, and (2) the server-side enrollment LOOKUP
 * failing (see `requireAal2IfEnrolled` in the Deno mirror) is now a refusal, not a
 * pass — that decision lives only in the Deno file since this client copy has no
 * I/O. Neither exception can fire for a real, live session token.
 */

/**
 * Decode the `aal` claim from a Supabase access-token JWT WITHOUT verifying its
 * signature. Safe to use ONLY after the token has already been validated by
 * `supabase.auth.getUser(token)` — this just reads a claim we already trust.
 * @param {unknown} token raw JWT string (`header.payload.signature`)
 * @returns {'aal1'|'aal2'|null} null when the claim cannot be read AND the token
 *   is not shaped like a real user token (no `sub`) — e.g. undecodable, or an
 *   anon/service-role key.
 */
export function decodeAalFromJwt(token) {
  if (typeof token !== 'string' || !token) return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4 !== 0) b64 += '=';
    const payload = JSON.parse(atob(b64));
    if (!payload || typeof payload !== 'object') return null;
    const aal = payload.aal;
    if (aal === 'aal2') return 'aal2';
    if (aal === 'aal1') return 'aal1';
    // Decoded payload shaped like a real user token (has `sub`) but the `aal`
    // claim is missing/invalid: GoTrue always stamps `aal` on user tokens, so
    // this can only be a forged/foreign token. Treat conservatively as aal1
    // rather than pass-through. A token with no `sub` (anon key, service key)
    // stays null so each function's own auth check is unaffected.
    if (payload.sub) return 'aal1';
    return null;
  } catch {
    return null;
  }
}

/**
 * Decide whether to BLOCK an action for failing AAL2.
 * @param {{ aal: 'aal1'|'aal2'|null, hasVerifiedFactor: boolean, mode: 'platform'|'tenant' }} p
 * @returns {boolean} true = block (respond 403).
 *
 * - `platform`: super-admins are force-enrolled, so any *confirmed* aal1 is a bypass → block.
 * - `tenant`: block only when the caller is BOTH enrolled AND at a *confirmed* aal1.
 * Undecodable aal (null) or aal2 NEVER blocks (fail-open / already elevated).
 */
export function shouldBlockForAal2({ aal, hasVerifiedFactor, mode }) {
  if (aal !== 'aal1') return false; // aal2 or undecodable → never block
  if (mode === 'platform') return true;
  return hasVerifiedFactor === true;
}
